import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown, Message, Placeholder } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import { PiFileCsvBold, PiMicrosoftExcelLogoFill } from 'react-icons/pi';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import SurveyForm from '../components/surveys/SurveyForm';
import SurveyLinkButton from '../components/surveys/SurveyLinkButton';
import { useSurveyResponses } from '../hooks/useSurveys';
import { STATE_LABELS, formatDate, questionNumbers } from '../lib/surveys';
import { showToast } from '../utils/toast';
import './SurveyResponsesPage.css';

const STATUS = {
  submitted: 'Responded',
  in_progress: 'Started',
  not_started: 'Not started',
};

const formatDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '');

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

// One answer as text, for the table and the CSV.
const answerText = (block, value) => {
  if (value === null || value === undefined || value === '') return '';
  const label = (id) => block.options?.find((o) => o.id === id)?.label || id;
  switch (block.kind) {
    case 'choice':
      return Array.isArray(value) ? value.map(label).join('; ') : label(value);
    case 'rating':
      return `${value} of 5`;
    case 'ranking':
      return (Array.isArray(value) ? value : []).map((id, i) => `${i + 1}. ${label(id)}`).join('; ');
    case 'signature':
      return value?.name ? `${value.name} (${formatDateTime(value.signedAt)})` : '';
    default:
      return String(value);
  }
};

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** What came back for a survey: how each question was answered, and who has responded. */
export default function SurveyResponsesPage() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useSurveyResponses(surveyId);
  const [viewing, setViewing] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);

  const survey = data?.survey;
  const anonymous = survey?.anonymous === true;
  const openLink = (survey?.allowLinkResponses || survey?.audience === 'link') && survey?.audience !== 'all';
  const people = useMemo(() => (Array.isArray(data?.people) ? data.people : []), [data]);
  // What the summaries read: each sent answer set. On an anonymous survey these belong to nobody.
  const responded = useMemo(() => (anonymous
    ? (data?.anonymousAnswers || []).map((answers, i) => ({ employeeId: `anon-${i}`, name: `Response ${i + 1}`, answers }))
    : people.filter((p) => p.status === 'submitted')), [anonymous, data, people]);
  const respondedPeople = people.filter((p) => p.status === 'submitted').length;
  const questions = useMemo(() => (survey?.content || []).filter((b) => b.type === 'question'), [survey]);
  const numbers = useMemo(() => questionNumbers(survey?.content || []), [survey]);

  const exportRows = () => {
    const prompts = questions.map((q) => `Q${numbers.get(q.id)}. ${q.prompt}`);
    // Anonymous: one row per answer set, no names (who responded is not in the file at all).
    const header = anonymous ? ['Response', ...prompts] : ['Name', 'Email', 'Status', 'Sent', ...prompts];
    const rows = anonymous
      ? responded.map((r) => [r.name, ...questions.map((q) => answerText(q, r.answers?.[q.id]))])
      : people.map((p) => [
      p.name,
      p.email,
      STATUS[p.status],
      p.submittedAt ? formatDateTime(p.submittedAt) : '',
      ...questions.map((q) => answerText(q, p.answers?.[q.id])),
    ]);
    return [header, ...rows];
  };

  const exportFilename = (extension) => `${(survey?.title || 'survey').replace(/[\\/:*?"<>|]+/g, '_')} responses.${extension}`;

  const exportCsv = () => {
    const csv = exportRows().map((row) => row.map(csvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename('csv');
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = async () => {
    setExportingExcel(true);
    try {
      const XLSX = await import('xlsx');
      const rows = exportRows();
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      worksheet['!cols'] = rows[0].map((heading) => ({ wch: Math.min(60, Math.max(18, heading.length)) }));
      worksheet['!autofilter'] = { ref: worksheet['!ref'] };
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Responses');
      XLSX.writeFile(workbook, exportFilename('xlsx'));
    } catch (failure) {
      showToast('error', String(failure?.message || failure || 'Unable to export survey results.'));
    } finally {
      setExportingExcel(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bdc-page">
        <Placeholder.Paragraph rows={8} active />
      </div>
    );
  }
  if (error || !survey) {
    return (
      <div className="bdc-page">
        <PageHeader crumbs={[{ label: 'Home', to: '/' }, { label: 'Surveys', to: '/surveys' }, { label: 'Responses' }]} title="Responses" />
        <Message type="error" showIcon>{String(error?.message || 'Those responses could not be loaded.')}</Message>
      </div>
    );
  }

  const started = people.filter((p) => p.status === 'in_progress').length;
  const exportDisabled = exportingExcel || (anonymous ? !responded.length : !people.length);

  return (
    <div className="bdc-page svr">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Surveys', to: '/surveys' },
          { label: survey.title, to: `/surveys/${survey.id}/edit` },
          { label: 'Responses' },
        ]}
        icon="bi-bar-chart"
        title="Responses"
        note={survey.title}
        backTo={`/surveys/${survey.id}/edit`}
        actions={(
          <>
            <SurveyLinkButton survey={survey} />
            <button type="button" className="bdc-btn" onClick={() => navigate(`/surveys/${survey.id}/edit`)}>
              <i className="bi bi-pencil" aria-hidden="true" />
              Edit survey
            </button>
            <Dropdown
              placement="bottomEnd"
              disabled={exportDisabled}
              renderToggle={(props, ref) => (
                <button {...props} ref={ref} type="button" className="bdc-btn" disabled={exportDisabled}>
                  <i className="bi bi-download" aria-hidden="true" />
                  {exportingExcel ? 'Exporting...' : 'Export'}
                  <i className="bi bi-chevron-down" aria-hidden="true" />
                </button>
              )}
            >
              <Dropdown.Item eventKey="csv" onSelect={exportCsv} icon={<PiFileCsvBold size={20} className="svr-export__csv" aria-hidden="true" />}>
                CSV (.csv)
              </Dropdown.Item>
              <Dropdown.Item eventKey="excel" onSelect={exportExcel} icon={<PiMicrosoftExcelLogoFill size={20} className="svr-export__excel" aria-hidden="true" />}>
                Excel (.xlsx)
              </Dropdown.Item>
            </Dropdown>
          </>
        )}
      />

      <div className="svr-stats">
        <Stat label="Status" value={STATE_LABELS[survey.state]} sub={survey.closesOn ? `${survey.state === 'closed' ? 'Closed' : 'Closes'} ${formatDate(survey.closesOn)}` : 'No closing date'} />
        <Stat label={openLink ? 'Participants' : 'Sent to'} value={people.length} sub={survey.audience === 'all' ? 'All Employees' : openLink ? 'Invitees and link participants' : 'Selected people'} />
        <Stat label="Responded" value={respondedPeople} sub={openLink ? 'Submitted responses' : `${pct(respondedPeople, people.length)}% of them`} bar={openLink ? null : pct(respondedPeople, people.length)} />
        <Stat label="Started, not sent" value={started} sub={openLink ? 'Not yet submitted' : `${people.length - respondedPeople - started} not started`} />
      </div>

      {anonymous ? (
        <div className="svr-anon" role="note">
          <i className="bi bi-incognito" aria-hidden="true" />
          <span>
            <strong>Anonymous survey.</strong> Answers are stored apart from the people who sent them, so they are shown
            without names and in no particular order. The People list shows only who has responded.
          </span>
        </div>
      ) : null}

      <Section title="By question" count={questions.length}>
        {responded.length === 0 ? (
          <div className="svr-empty">No responses yet. Answers show here once people send them.</div>
        ) : (
          <div className="svr-questions">
            {questions.map((q) => (
              <QuestionSummary key={q.id} question={q} number={numbers.get(q.id)} responses={responded} anonymous={anonymous} />
            ))}
          </div>
        )}
      </Section>

      <Section title="People" count={people.length}>
        <div className="nsi-table-wrap">
          <table className="nsi-table svr-people">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Sent</th>
                <th aria-label="Answers" />
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.employeeId}>
                  <td>
                    <div className="svr-person">{p.name}</div>
                    {p.email ? <div className="svr-person__email">{p.email}</div> : null}
                  </td>
                  <td>
                    <span className={`svr-status svr-status--${p.status}`}>{STATUS[p.status]}</span>
                  </td>
                  <td>{p.submittedAt ? formatDateTime(p.submittedAt) : '—'}</td>
                  <td className="svr-people__action">
                    {p.status === 'submitted' && !anonymous ? (
                      <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => setViewing(p)}>
                        <i className="bi bi-eye" aria-hidden="true" />
                        Answers
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Modal open={Boolean(viewing)} onClose={() => setViewing(null)} size="md" overflow>
        <Modal.Header>
          <Modal.Title>{viewing?.name}&rsquo;s answers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewing ? (
            <>
              <p className="svr-viewing">Sent {formatDateTime(viewing.submittedAt)}</p>
              <SurveyForm surveyId={survey.id} content={survey.content} answers={viewing.answers || {}} readOnly />
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setViewing(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function Stat({ label, value, sub, bar = null }) {
  return (
    <div className="svr-stat">
      <span className="svr-stat__label">{label}</span>
      <strong className="svr-stat__value">{value}</strong>
      <span className="svr-stat__sub">{sub}</span>
      {bar !== null ? (
        <span className="svr-bar">
          <span style={{ width: `${bar}%` }} />
        </span>
      ) : null}
    </div>
  );
}

function QuestionSummary({ question, number, responses, anonymous }) {
  const answered = responses.filter((r) => {
    const v = r.answers?.[question.id];
    return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length);
  });

  return (
    <article className="svr-q">
      <header className="svr-q__head">
        <span className="svr-q__number">Q{number}</span>
        <h3 className="svr-q__prompt">{question.prompt}</h3>
        <span className="svr-q__count">
          {answered.length} answer{answered.length === 1 ? '' : 's'}
        </span>
      </header>
      <QuestionBody question={question} answered={answered} anonymous={anonymous} />
    </article>
  );
}

function Bars({ rows, total }) {
  return (
    <div className="svr-bars">
      {rows.map((row) => (
        <div key={row.key} className="svr-bars__row">
          <span className="svr-bars__label">{row.label}</span>
          <span className="svr-bar svr-bar--wide">
            <span style={{ width: `${pct(row.count, total)}%` }} />
          </span>
          <span className="svr-bars__value">
            {row.count} <span>({pct(row.count, total)}%)</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function QuestionBody({ question, answered, anonymous }) {
  const total = answered.length;
  if (!total) return <div className="svr-q__none">No answers yet.</div>;

  if (question.kind === 'choice') {
    const rows = question.options.map((o) => ({
      key: o.id,
      label: o.label,
      count: answered.filter((r) => {
        const v = r.answers[question.id];
        return Array.isArray(v) ? v.includes(o.id) : v === o.id;
      }).length,
    }));
    return <Bars rows={rows} total={total} />;
  }

  if (question.kind === 'rating') {
    const values = answered.map((r) => Number(r.answers[question.id]));
    const average = values.reduce((a, b) => a + b, 0) / values.length;
    return (
      <div className="svr-rating">
        <div className="svr-rating__avg">
          <strong>{average.toFixed(1)}</strong>
          <span className="svr-rating__stars" aria-label={`${average.toFixed(1)} of 5 stars`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <i key={n} className={`bi ${average >= n - 0.25 ? 'bi-star-fill' : average >= n - 0.75 ? 'bi-star-half' : 'bi-star'}`} aria-hidden="true" />
            ))}
          </span>
          <span className="svr-rating__of">average of {total}</span>
        </div>
        <Bars
          rows={[5, 4, 3, 2, 1].map((n) => ({ key: n, label: `${n} star${n === 1 ? '' : 's'}`, count: values.filter((v) => v === n).length }))}
          total={total}
        />
      </div>
    );
  }

  if (question.kind === 'ranking') {
    // Average position: lower is ranked more important.
    const rows = question.options
      .map((o) => {
        const positions = answered
          .map((r) => (Array.isArray(r.answers[question.id]) ? r.answers[question.id].indexOf(o.id) : -1))
          .filter((i) => i >= 0)
          .map((i) => i + 1);
        const avg = positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : null;
        const first = positions.filter((p) => p === 1).length;
        return { key: o.id, label: o.label, avg, first };
      })
      .sort((a, b) => (a.avg ?? 99) - (b.avg ?? 99));
    return (
      <ol className="svr-ranking">
        {rows.map((row, i) => (
          <li key={row.key}>
            <span className="svr-ranking__place">{i + 1}</span>
            <span className="svr-ranking__label">{row.label}</span>
            <span className="svr-ranking__avg">
              average place {row.avg?.toFixed(1) ?? '—'}
              {row.first ? ` · first for ${row.first}` : ''}
            </span>
          </li>
        ))}
      </ol>
    );
  }

  if (question.kind === 'signature') {
    return (
      <ul className="svr-list">
        {answered.map((r) => (
          <li key={r.employeeId}>
            <i className="bi bi-pen" aria-hidden="true" />
            <span className="svr-signature">{r.answers[question.id]?.name}</span>
            <span className="svr-list__who">
              {r.name} · {formatDateTime(r.answers[question.id]?.signedAt)}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="svr-list svr-list--text">
      {answered.map((r) => (
        <li key={r.employeeId}>
          <span className="svr-list__text">{String(r.answers[question.id])}</span>
          {anonymous ? null : <span className="svr-list__who">{r.name}</span>}
        </li>
      ))}
    </ul>
  );
}
